import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError, expand, reduce } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';

import {
  Project,
  ProjectsResponse,
  ProjectDetail,
  ProjectDetailResponse,
  CreateProjectPayload,
  UpdateProjectPayload,
  ProjectListFilters,
} from '../models/projects.model';

@Injectable({ providedIn: 'root' })
export class ProjectsService {
  private readonly baseUrl = environment.apiBaseUrl;

  constructor(private http: HttpClient) {}

  getProjects(
    pageNumber: number = 1,
    pageSize: number = 20,
    filters?: ProjectListFilters,
  ): Observable<ProjectsResponse | null> {
    let params = new HttpParams()
      .set('pageNumber', String(pageNumber))
      .set('pageSize', String(pageSize));

    const searchTerm = String(filters?.searchTerm ?? '').trim();
    if (searchTerm) {
      params = params.set('searchTerm', searchTerm);
    }

    return this.http
      .get<ProjectsResponse>(`${this.baseUrl}/api/projects/all`, { params })
      .pipe(
        catchError((err) => {
          console.error('Error fetching projects:', err);
          return of(null);
        }),
      );
  }

  getProjectById(id: number): Observable<ProjectDetail | null> {
    return this.http
      .get<ProjectDetailResponse>(`${this.baseUrl}/api/projects/${id}`)
      .pipe(
        map((res) => res.data),
        catchError((err) => {
          console.error('Error fetching project details:', err);
          return of(null);
        }),
      );
  }

  createProject(payload: CreateProjectPayload): Observable<boolean> {
    return this.http
      .post<{ status: string }>(`${this.baseUrl}/api/projects`, payload)
      .pipe(
        map(() => true),
        catchError((err) => {
          console.error('Error creating project:', err);
          return of(false);
        }),
      );
  }

  updateProject(id: number, payload: UpdateProjectPayload): Observable<boolean> {
    return this.http
      .patch<{ status: string }>(`${this.baseUrl}/api/projects/${id}`, payload)
      .pipe(
        map(() => true),
        catchError((err) => {
          console.error('Error updating project:', err);
          return of(false);
        }),
      );
  }

  getAllProjects(): Observable<Project[]> {
    return this.fetchAllPages<ProjectsResponse, Project>(`${this.baseUrl}/api/projects/all`);
  }

  private fetchAllPages<
    T extends { currentPage: number; totalPages: number; data: K[] },
    K,
  >(url: string): Observable<K[]> {
    const pageSize = 50;

    return this.http
      .get<T>(url, {
        params: new HttpParams()
          .set('pageNumber', 1)
          .set('pageSize', pageSize),
      })
      .pipe(
        expand((res) =>
          res.currentPage < res.totalPages
            ? this.http.get<T>(url, {
                params: new HttpParams()
                  .set('pageNumber', res.currentPage + 1)
                  .set('pageSize', pageSize),
              })
            : of(),
        ),
        map((res) => res.data),
        reduce((all, page) => [...all, ...page], [] as K[]),
        catchError((err) => {
          console.error('Error fetching paginated projects:', err);
          return of([]);
        }),
      );
  }
}